'use client';
// AmanChain Founder Console — only the wallet that launched the network
// can administer it. Verification = real seed decryption, same as moving
// funds. Every governance action is a signed on-chain transaction.
import { useState } from 'react';
import {
  ShieldCheck, KeyRound, SlidersHorizontal, Snowflake, Loader2,
  Copy, Check, Globe, Terminal, Link2, PackageOpen, HardDriveDownload,
  FileArchive, RotateCcw, DatabaseBackup, Bot, Coins, Vault,
} from 'lucide-react';
import { toast } from 'sonner';
import { post, fetchJson, usePoll, fmtAmt, fmtInt, SectionHeader, StatCard, Mono, timeAgo, CopyBtn } from './shared';
import { useSession } from './session';
import type { Net, AdminVerifyT, ApiKeyT, TokenT } from './types';
import { useEffect } from 'react';

const netQ = (net: Net) => `net=${net}`;

// Internal founder-console sections — the console is split so settings,
// bridge control, AI services, recovery snapshots and API keys each get a clear place.
type AdminTab = 'overview' | 'bridge' | 'services' | 'snapshots' | 'keys';
const ADMIN_TABS: Array<{ id: AdminTab; label: string; icon: typeof ShieldCheck }> = [
  { id: 'overview', label: 'Overview & Governance', icon: SlidersHorizontal },
  { id: 'bridge', label: 'Bridge Control', icon: Link2 },
  { id: 'services', label: 'AI Services & Pricing', icon: Bot },
  { id: 'snapshots', label: 'Snapshots & Recovery', icon: DatabaseBackup },
  { id: 'keys', label: 'API & Keys', icon: KeyRound },
];

export function AdminConsole({ net }: { net: Net }) {
  const { session, isFounder } = useSession();
  const [auth, setAuth] = useState<AdminVerifyT | null>(null);
  const [busy, setBusy] = useState(false);
  const [adminTab, setAdminTab] = useState<AdminTab>('overview');

  // The tab is hidden for everyone except the founder session — verification here is
  // defense in depth: the node decrypts the founder seed before granting the console.
  const unlock = async (walletId: string, password: string) => {
    if (!walletId || !password) return;
    setBusy(true);
    try {
      const r = await post<AdminVerifyT>('/api/admin/verify', { net, walletId, password });
      setAuth(r);
      toast.success(`Founder session verified — welcome, ${r.name}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Verification failed');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (session?.walletId && session?.password && !auth) {
      void unlock(session.walletId, session.password);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.walletId]);

  if (!session || !isFounder) {
    return (
      <div className="max-w-xl mx-auto space-y-6">
        <SectionHeader icon={ShieldCheck} title="Founder Console"
          desc="This console exists only for the wallet that launched the network. Connect the founder wallet — its session unlocks the console automatically." />
        <div className="glass p-8 text-center space-y-2">
          <ShieldCheck className="size-8 text-muted-foreground mx-auto" />
          <div className="font-black">Restricted — founder session required</div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Governance is validated against the founder address recorded in genesis. Any other wallet — or no wallet — sees nothing here.
          </p>
        </div>
      </div>
    );
  }

  if (!auth) {
    return (
      <div className="max-w-xl mx-auto space-y-6">
        <SectionHeader icon={ShieldCheck} title="Founder Console"
          desc="Verifying your founder authority — the node decrypts the founder seed to prove ownership, then discards it." />
        <div className="glass p-8 text-center text-sm text-muted-foreground inline-flex items-center gap-2 justify-center w-full">
          {busy && <Loader2 className="size-4 animate-spin" />} Verifying founder authority…
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SectionHeader icon={ShieldCheck} title={`Founder Console — ${auth.name}`}
        desc={`Verified founder of ${net}. Governance actions are on-chain transactions signed by your wallet.`}
        action={<button onClick={() => setAuth(null)}
          className="rounded-lg ring-1 ring-border px-3 py-1.5 text-xs font-bold hover:ring-primary/40 transition">Lock</button>} />

      {/* Internal sections — separate settings from bridge from recovery from API */}
      <div className="flex gap-1 overflow-x-auto rounded-xl ring-1 ring-border bg-muted/30 p-1" role="tablist" aria-label="Admin sections">
        {ADMIN_TABS.map(t => (
          <button key={t.id} role="tab" aria-selected={adminTab === t.id} onClick={() => setAdminTab(t.id)}
            className={`flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-xs font-bold whitespace-nowrap transition ${adminTab === t.id
              ? 'bg-primary/15 text-emerald-400 ring-1 ring-primary/40'
              : 'text-muted-foreground hover:text-foreground'}`}>
            <t.icon className="size-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {adminTab === 'overview' && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard icon={ShieldCheck} label="Founder address" accent="emerald" value={<Mono className="text-foreground">{auth.address.slice(0, 14)}…</Mono>} sub="Recorded in the genesis block" />
            <StatCard icon={Globe} label="Chain-ID" accent="sky" value={auth.network.chainId} sub={`Height ${fmtInt(auth.network.height)}`} />
            <StatCard icon={SlidersHorizontal} label="Token deployment fee" accent="violet" value={<>{fmtAmt(auth.params.tokenCreateFee)} <span className="text-xs text-muted-foreground">AMAN</span></>} sub="Burned 100% on each deployment" />
            <StatCard icon={FlameIco} label="Fee burn rate" accent="rose" value={`${(auth.params.feeBurnRate * 100).toFixed(0)}%`} sub="Of every transfer fee destroyed" />
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            <GovernancePanel net={net} auth={auth} password={session.password} />
            <FreezePanel net={net} auth={auth} password={session.password} />
          </div>
        </>
      )}
      {adminTab === 'bridge' && <BridgePanel net={net} auth={auth} walletId={session.walletId} password={session.password} />}
      {adminTab === 'services' && (
        <>
          <DiscoveryPanel net={net} />
          <ServicesPanel net={net} walletId={session.walletId} password={session.password} />
        </>
      )}
      {adminTab === 'snapshots' && <SnapshotPanel walletId={session.walletId} password={session.password} />}
      {adminTab === 'keys' && (
        <>
          <KeysPanel net={net} auth={auth} password={session.password} />
          <ApiDocs />
        </>
      )}
    </div>
  );
}

function FlameIco({ className }: { className?: string }) {
  return <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" aria-hidden><path d="M12 3c1 3-2 4-2 7a4 4 0 0 0 8 .5C18 7 14 6 12 3z" /><path d="M8 14a4 4 0 1 0 8 0" /></svg>;
}

// ============================================================
// ServicesPanel — AI Services & Pricing (v2.9)
// The founder sets the per-call x402 price of every node-operated
// service and can pause/reactivate it. Every change is a REAL
// on-chain agent_service transaction — consensus-enforced pricing,
// visible to all agents through the x402 catalog instantly.
// ============================================================
interface ServiceRow {
  serviceId: string; name: string; description: string; capability: string;
  priceAman: number; priceUsd: number | null; active: boolean;
  calls: number; volumeAman: number; agentId: string; agentName: string;
  nodeOperated: boolean; editable: boolean; lastCalledAt: number | null;
}

// ============================================================
// DiscoveryPanel — x402 external agent discovery (v2.10)
// The five STANDARD machine-readable locations external agent
// frameworks and LLM crawlers probe (they do NOT search the web —
// they fetch these exact paths). Each row is health-checked LIVE
// from this browser and shows how many services the outside world
// sees. Honest about the Coinbase Bazaar: listing there requires
// the founder's own CDP API keys — every other surface is already
// automatic and generated live from chain state.
// ============================================================
interface DiscoveryProbe { ok: boolean; detail: string }

const DISCOVERY_PATHS: Array<{ path: string; label: string; hint: string }> = [
  { path: '/.well-known/x402', label: 'x402 Well-Known', hint: 'أول مسار يفحصه أي وكيل x402 خارجي — the first location external x402 agents probe' },
  { path: '/x402/discovery', label: 'Bazaar Resource Directory', hint: 'resources[] بنفس شكل مفهرس x402scan وCDP' },
  { path: '/services.json', label: 'Flat Service Catalog', hint: 'GET واحد — كل خدمة بسعرها الحي' },
  { path: '/.well-known/agent.json', label: 'A2A Agent Card', hint: 'بطاقة بروتوكول Google Agent-to-Agent' },
  { path: '/llms.txt', label: 'llms.txt', hint: 'الفهرس الذي تقرأه ChatGPT / Claude / Perplexity' },
  { path: '/api/x402', label: 'x402 Payment Catalog', hint: 'المواصفة الكاملة + accepts[] + إحصاءات النداءات' },
];

function DiscoveryPanel({ net }: { net: Net }) {
  const [status, setStatus] = useState<Record<string, DiscoveryProbe | 'loading'>>({});
  const [cdp, setCdp] = useState<boolean | null>(null);
  const [advertised, setAdvertised] = useState<string | null>(null);
  const [stamp, setStamp] = useState(0);

  useEffect(() => {
    let alive = true;
    const check = async (p: string): Promise<DiscoveryProbe> => {
      try {
        const r = await fetch(p, { cache: 'no-store' });
        if (!r.ok) return { ok: false, detail: `HTTP ${r.status}` };
        if (p === '/llms.txt') {
          const t = await r.text();
          return { ok: t.includes('## Services'), detail: `${t.split('\n').filter(l => l.startsWith('- [')).length} services listed for LLM crawlers` };
        }
        const j = await r.json() as { resources?: Array<{ endpoint?: string }>; services?: unknown[]; skills?: unknown[]; facilitators?: Array<{ mode?: string }> };
        if (p === '/.well-known/x402') {
          setCdp((j.facilitators ?? []).some(f => f.mode === 'cdp'));
          const ep = j.resources?.[0]?.endpoint;
          if (ep) { try { setAdvertised(new URL(ep).origin); } catch { /* ignore */ } }
        }
        const n = j.resources?.length ?? j.services?.length ?? j.skills?.length ?? 0;
        return { ok: n > 0, detail: `${n} services visible to external agents` };
      } catch { return { ok: false, detail: 'unreachable' }; }
    };
    Promise.resolve().then(() => {
      if (!alive) return;
      setStatus(Object.fromEntries(DISCOVERY_PATHS.map(p => [p.path, 'loading' as const])));
    });
    void Promise.all(DISCOVERY_PATHS.map(async ({ path }) => ({ path, r: await check(path) })))
      .then(rs => { if (alive) setStatus(Object.fromEntries(rs.map(({ path, r }) => [path, r]))); });
    return () => { alive = false; };
  }, [net, stamp]);

  return (
    <div className="glass p-5 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="font-black flex items-center gap-2"><Globe className="size-4 text-sky-400" /> x402 Discovery &amp; Bazaar — بث الخدمات للوكلاء الخارجيين</div>
          <p className="text-[0.68rem] text-muted-foreground mt-0.5">
            The agents outside this platform never browse it — they fetch the standard discovery files below. All five are generated live from the chain: change a price here and the outside world sees it on the next fetch.
          </p>
        </div>
        <button onClick={() => setStamp(s => s + 1)} className="rounded-lg ring-1 ring-border px-3 py-1.5 text-xs font-bold hover:ring-primary/40 transition inline-flex items-center gap-1.5">
          <Loader2 className={`size-3 ${Object.values(status).some(s => s === 'loading') ? 'animate-spin' : ''}`} /> Re-check live
        </button>
      </div>
      <div className="grid gap-1.5">
        {DISCOVERY_PATHS.map(({ path, label, hint }) => {
          const st = status[path];
          const abs = typeof window === 'undefined' ? path : `${window.location.origin}${path}`;
          return (
            <div key={path} className="flex items-center gap-2 rounded-lg ring-1 ring-border bg-background/40 px-3 py-2">
              <span className={`size-2 rounded-full shrink-0 ${st === 'loading' ? 'bg-amber-400 animate-pulse' : st?.ok ? 'bg-emerald-400' : 'bg-rose-400'}`} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-black">{label}</span>
                  <Mono className="text-[0.62rem] text-sky-400">{path}</Mono>
                </div>
                <div className="text-[0.62rem] text-muted-foreground truncate">{st === 'loading' ? 'checking…' : st?.detail ?? hint}</div>
              </div>
              <CopyBtn text={abs} className="shrink-0" />
            </div>
          );
        })}
      </div>
      <div className="rounded-xl ring-1 ring-sky-500/20 bg-sky-500/5 p-3 space-y-1.5 text-[0.66rem] leading-relaxed">
        <div className="font-black text-sky-400 text-[0.7rem]">الحقيقة الكاملة حول Bazaar — the honest Bazaar status</div>
        {advertised && typeof window !== 'undefined' && advertised !== window.location.origin && (
          <p className="rounded-lg bg-amber-500/10 ring-1 ring-amber-500/30 px-2.5 py-1.5 text-amber-300">
            <span className="font-black">ⓘ العناوين المعلنة مصدرها <Mono dir="ltr">PUBLIC_ORIGIN</Mono> في <Mono dir="ltr">.env</Mono> ({advertised}) وتختلف عن نطاق هذه الجلسة — صحيح إذا كنت تخدم من نطاق عام عبر بروكسي. عند تغيير النطاق حدّث <Mono dir="ltr">PUBLIC_ORIGIN</Mono> وأعد التشغيل.</span>
          </p>
        )}
        <p>
          <span className="font-black">✓ تلقائي الآن:</span> ملفات الاكتشاف الخمسة حية بعناوين المنصة العامة، وكل رد 402 يحمل مصفوفة <Mono>accepts[]</Mono> القياسية التي تقرأها أدوات x402 (جرّب <Mono>npx x402-fetch</Mono>). أي زاحف أو إطار عمل وكلاء يفحص المسارات القياسية يجد الكتالوج الكامل بالأسعار الحية.
        </p>
        <p>
          <span className="font-black">ⓘ Coinbase Bazaar — مفعّل الآن (v2.11):</span> مفاتيح CDP الخاصة بالمؤسس منشطة ومصادقة JWT حية مع <Mono dir="ltr">api.cdp.coinbase.com</Mono>. كل خدمة مقبولة الآن بمخطط <Mono>exact</Mono> القياسي: الوكيل الخارجي يدفع <Mono dir="ltr">USDC حقيقي على Base</Mono> وكوين باس تتحقق وتسوّي على السلسلة — وبعد أول معاملة حقيقية تُفهرس خدماتنا في Bazaar وتظهر على <Mono dir="ltr">x402scan.com</Mono> و<Mono dir="ltr">402index.io/directory</Mono> وعلى <Mono dir="ltr">api.cdp.coinbase.com/platform/v2/x402/discovery/resources</Mono>. عوائد الخدمات تهبط في عنوان تسوية مخصص مشتق من بذرة الخزائن (داخل ملف الاسترجاع نفسه) — منفصل عن ودائع الجسر ومضمون الاستخراج.
        </p>
        <p className="flex items-center gap-2">
          <span className={`rounded-full px-2 py-0.5 text-[0.6rem] font-black ${cdp ? 'bg-emerald-500/10 text-emerald-400' : 'bg-muted/60 text-muted-foreground'}`}>
            {cdp ? 'CDP active — USDC on Base settlement live (Bazaar after first real payment)' : 'CDP keys not set — node-native facilitator only'}
          </span>
        </p>
      </div>
    </div>
  );
}

function ServicesPanel({ net, walletId, password }: { net: Net; walletId: string; password: string }) {
  const [rows, setRows] = useState<ServiceRow[] | null>(null);
  const [amanUsd, setAmanUsd] = useState<number | null>(null);
  const [prices, setPrices] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  const load = async () => {
    try {
      const r = await fetchJson<{ services: ServiceRow[]; amanUsd: number | null }>(`/api/admin/services?${netQ(net)}`);
      setRows(r.services); setAmanUsd(r.amanUsd);
    } catch { /* first paint before launch is fine */ }
  };
  useEffect(() => { void load(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [net]);

  const savePrice = async (serviceId: string) => {
    const raw = (prices[serviceId] ?? '').trim();
    if (!(Number(raw) > 0)) { toast.error('Enter a positive price in AMAN'); return; }
    setBusy(serviceId);
    try {
      await post(`/api/admin/services?${netQ(net)}`, { walletId, password, serviceId, priceAman: Number(raw) });
      toast.success(`${serviceId} — price update broadcast (mines into the next block)`);
      setPrices(p => ({ ...p, [serviceId]: '' }));
      setTimeout(() => void load(), 2500);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Price update failed');
    } finally { setBusy(null); }
  };

  const toggleActive = async (serviceId: string, active: boolean) => {
    setBusy(serviceId);
    try {
      await post(`/api/admin/services?${netQ(net)}`, { walletId, password, serviceId, active });
      toast.success(`${serviceId} ${active ? 'activated' : 'paused'} — broadcast on-chain`);
      setTimeout(() => void load(), 2500);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Update failed');
    } finally { setBusy(null); }
  };

  const shown = (rows ?? []).filter(r =>
    !filter || r.serviceId.includes(filter.toLowerCase()) || r.name.toLowerCase().includes(filter.toLowerCase()) || r.capability.includes(filter.toLowerCase()));

  return (
    <div className="space-y-4">
      <div className="glass p-5 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="font-black flex items-center gap-2"><Bot className="size-4 text-primary" /> AI Services & Pricing</div>
            <p className="text-[0.68rem] text-muted-foreground mt-0.5">
              Set the x402 per-call price of every node-operated service (AMAN + live USD). Agents see the new price in the x402 catalog the moment the transaction mines.
            </p>
          </div>
          <button onClick={() => void load()} className="rounded-lg ring-1 ring-border px-3 py-1.5 text-xs font-bold hover:ring-primary/40 transition">Refresh</button>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-[0.66rem]">
          <span className="rounded-full bg-primary/10 text-primary ring-1 ring-primary/30 px-2.5 py-1 font-black">{rows?.length ?? '…'} services on-chain</span>
          <span className="rounded-full bg-emerald-500/10 text-emerald-400 ring-1 ring-emerald-500/30 px-2.5 py-1 font-black">{rows?.filter(r => r.nodeOperated).length ?? 0} node-operated (editable here)</span>
          {amanUsd !== null && <span className="rounded-full bg-muted/50 ring-1 ring-border px-2.5 py-1 font-bold">1 AMAN ≈ ${amanUsd.toFixed(4)}</span>}
          <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="filter by id / name / capability…" dir="ltr"
            className="rounded-lg bg-background ring-1 ring-input px-3 py-1.5 text-xs outline-none focus:ring-primary/50 w-56" />
        </div>
      </div>

      <div className="space-y-2">
        {shown.map(r => (
          <div key={r.serviceId} className="glass p-4 flex flex-col lg:flex-row lg:items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-black text-sm">{r.name}</span>
                <Mono className="text-[0.62rem] text-muted-foreground">{r.serviceId}</Mono>
                <span className="rounded-full bg-sky-500/10 text-sky-400 px-2 py-0.5 text-[0.6rem] font-black">{r.capability}</span>
                {r.nodeOperated
                  ? <span className="rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[0.6rem] font-black">NODE</span>
                  : <span className="rounded-full bg-violet-500/10 text-violet-400 px-2 py-0.5 text-[0.6rem] font-black">EXTERNAL</span>}
                <span className={`rounded-full px-2 py-0.5 text-[0.6rem] font-black ${r.active ? 'bg-emerald-500/10 text-emerald-400' : 'bg-rose-500/10 text-rose-400'}`}>{r.active ? 'ACTIVE' : 'PAUSED'}</span>
              </div>
              <p className="text-[0.66rem] text-muted-foreground mt-1 line-clamp-2">{r.description}</p>
              <div className="text-[0.62rem] text-muted-foreground mt-1">
                agent {r.agentName} · {r.calls} paid calls · {fmtAmt(r.volumeAman)} AMAN earned
                {r.lastCalledAt ? ` · last call ${timeAgo(r.lastCalledAt)}` : ''}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <div className="text-right">
                <div className="font-black text-sm" dir="ltr">{r.priceAman} <span className="text-[0.62rem] text-muted-foreground">AMAN/call</span></div>
                {r.priceUsd !== null && <div className="text-[0.62rem] text-emerald-400 font-bold" dir="ltr">≈ ${r.priceUsd.toFixed(4)}</div>}
              </div>
              {r.editable ? (
                <>
                  <div className="flex items-center gap-1">
                    <input value={prices[r.serviceId] ?? ''} onChange={e => setPrices(p => ({ ...p, [r.serviceId]: e.target.value.replace(/[^0-9.]/g, '') }))}
                      placeholder="new price" dir="ltr" inputMode="decimal"
                      className="w-24 rounded-lg bg-background ring-1 ring-input px-2 py-1.5 text-[0.68rem] outline-none focus:ring-primary/50" />
                    <button onClick={() => void savePrice(r.serviceId)} disabled={busy === r.serviceId}
                      className="rounded-lg bg-primary text-primary-foreground px-3 py-1.5 text-[0.66rem] font-black disabled:opacity-50 inline-flex items-center gap-1">
                      {busy === r.serviceId ? <Loader2 className="size-3 animate-spin" /> : <Coins className="size-3" />} Set
                    </button>
                  </div>
                  <button onClick={() => void toggleActive(r.serviceId, !r.active)} disabled={busy === r.serviceId}
                    className={`rounded-lg px-3 py-1.5 text-[0.66rem] font-black ring-1 disabled:opacity-50 ${r.active ? 'bg-rose-500/10 text-rose-400 ring-rose-500/30' : 'bg-emerald-500/10 text-emerald-400 ring-emerald-500/30'}`}>
                    {r.active ? 'Pause' : 'Activate'}
                  </button>
                </>
              ) : (
                <span className="text-[0.62rem] text-muted-foreground max-w-[140px]">priced & managed by its external owner</span>
              )}
            </div>
          </div>
        ))}
        {rows !== null && shown.length === 0 && (
          <div className="glass p-6 text-center text-sm text-muted-foreground">
            No services match this filter — the v2.9 fleet registers itself on-chain within a minute of boot.
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// SnapshotPanel — full-platform recovery system (v2.8)
// One click downloads the COMPLETE platform as a single .zip:
// source code, database (wallets + bridge vaults + history),
// configs and .env. Stored snapshots are listed for re-download.
// This is the answer to environment rollbacks: nothing is ever
// lost because the latest version can always be re-uploaded.
// ============================================================
interface SnapshotInfo { file: string; bytes: number; createdAt: number; version: string; }
interface SnapshotView { version: string; name: string; features: number; commit: string; snapshots: SnapshotInfo[]; }

function fmtBytes(n: number): string {
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(2)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${n} B`;
}

function SnapshotPanel({ walletId, password }: { walletId: string; password: string }) {
  const [view, setView] = useState<SnapshotView | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = async () => {
    try {
      const r = await post<SnapshotView>('/api/admin/backup', { action: 'list', walletId, password });
      setView(r); setErr(null);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Snapshot list failed'); }
  };
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const saveBlob = (r: Response, fallback: string) => {
    const cd = r.headers.get('content-disposition') ?? '';
    const m = cd.match(/filename="?([^";]+)"?/);
    r.blob().then(b => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(b);
      a.download = m?.[1] ?? fallback;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 4000);
    });
  };

  const downloadLatest = async () => {
    setBusy('create');
    try {
      const r = await fetch('/api/admin/backup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'create', walletId, password }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? 'Snapshot failed');
      saveBlob(r, `amanchain-v${view?.version ?? 'latest'}.zip`);
      toast.success('Full platform snapshot downloaded — keep it safe');
      setTimeout(() => void load(), 800);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Snapshot failed');
    } finally { setBusy(null); }
  };

  const downloadStored = async (file: string) => {
    setBusy(file);
    try {
      const r = await fetch('/api/admin/backup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'download', walletId, password, file }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? 'Download failed');
      saveBlob(r, file);
      toast.success('Stored snapshot downloaded');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Download failed');
    } finally { setBusy(null); }
  };

  return (
    <div className="space-y-4">
      {/* Primary recovery card — the download button */}
      <div className="glass p-5 space-y-3 ring-1 ring-emerald-500/30">
        <div className="flex items-center gap-2 text-sm font-bold">
          <PackageOpen className="size-4 text-emerald-400" /> Full platform snapshot
          {view && <span className="rounded-full bg-emerald-500/10 text-emerald-400 px-2 py-0.5 text-[0.62rem] font-black">v{view.version}</span>}
        </div>
        <p className="text-[0.7rem] text-muted-foreground leading-relaxed">
          يضغط هذا الزر المنصة كاملة في ملف واحد: الكود المصدري + قاعدة البيانات (كل المحافظ، مفاتيح خزائن الجسر المشفّرة، سجل المعاملات) + الإعدادات و <Mono>.env</Mono>.
          إذا رجعت المنصة لنسخة قديمة: ارفع هذا الملف وفك الضغط في جذر المشروع ثم <Mono>bun install && bun run dev</Mono> — وكل شيء يعود كما كان بالضبط.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[0.62rem] text-muted-foreground">
          <span className="rounded-lg bg-muted/40 px-2.5 py-1.5 flex items-center gap-1.5"><FileArchive className="size-3.5 text-emerald-400" /> src/ — full source</span>
          <span className="rounded-lg bg-muted/40 px-2.5 py-1.5 flex items-center gap-1.5"><DatabaseBackup className="size-3.5 text-emerald-400" /> db/ — wallets + bridge vaults</span>
          <span className="rounded-lg bg-muted/40 px-2.5 py-1.5 flex items-center gap-1.5"><RotateCcw className="size-3.5 text-emerald-400" /> configs + .env</span>
          <span className="rounded-lg bg-muted/40 px-2.5 py-1.5 flex items-center gap-1.5"><PackageOpen className="size-3.5 text-emerald-400" /> RESTORE.md roadmap</span>
        </div>
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <button onClick={() => void downloadLatest()} disabled={busy !== null}
            className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 text-zinc-950 font-black px-5 py-3 text-sm hover:bg-emerald-400 transition disabled:opacity-40 shadow-lg shadow-emerald-500/20">
            {busy === 'create' ? <Loader2 className="size-4 animate-spin" /> : <HardDriveDownload className="size-4" />}
            تحميل آخر نسخة من المنصة {view ? `v${view.version}` : ''}
          </button>
          <span className="text-[0.65rem] text-muted-foreground" dir="ltr">
            {view && <>commit <Mono>{view.commit}</Mono> · {view.features} features</>}
          </span>
        </div>
      </div>

      {err && <div className="rounded-xl bg-rose-500/10 text-rose-400 ring-1 ring-rose-500/30 p-2.5 text-xs font-bold">{err}</div>}

      {/* Stored snapshots */}
      <div className="glass p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-sm font-bold flex items-center gap-2"><FileArchive className="size-4 text-sky-400" /> Server-stored snapshots</div>
          <button onClick={() => void load()} className="rounded-lg ring-1 ring-border px-3 py-1.5 text-xs font-bold hover:ring-primary/40 transition">Refresh</button>
        </div>
        <p className="text-[0.66rem] text-muted-foreground">
          Every snapshot created here is also kept on the server under <Mono>backups/</Mono> — if the platform reverts, the newest one below is the version to re-upload.
        </p>
        {(view?.snapshots ?? []).length === 0 ? (
          <div className="text-xs text-muted-foreground">No stored snapshots yet — press the button above to create the first one.</div>
        ) : (
          <div className="space-y-2">
            {view!.snapshots.map(s => (
              <div key={s.file} className="flex items-center justify-between gap-2 rounded-lg bg-muted/40 px-3 py-2">
                <div className="min-w-0">
                  <div className="text-xs font-bold flex items-center gap-2">
                    v{s.version}
                    <span className="rounded-full bg-sky-500/10 text-sky-400 px-2 py-0.5 text-[0.6rem]">{fmtBytes(s.bytes)}</span>
                  </div>
                  <Mono className="text-[0.62rem] text-muted-foreground">{s.file} · {timeAgo(s.createdAt)}</Mono>
                </div>
                <button onClick={() => void downloadStored(s.file)} disabled={busy !== null}
                  className="rounded-lg bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/40 px-3 py-1.5 text-[0.66rem] font-black disabled:opacity-40 shrink-0">
                  {busy === s.file ? <Loader2 className="size-3.5 animate-spin" /> : 'Download'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recovery roadmap */}
      <div className="glass p-5 space-y-2">
        <div className="text-sm font-bold flex items-center gap-2"><RotateCcw className="size-4 text-amber-400" /> Recovery roadmap — when the platform reverts to an old version</div>
        <ol className="text-[0.7rem] text-muted-foreground leading-relaxed list-decimal list-inside space-y-1.5">
          <li>Get the latest snapshot: download it from this panel, or use your own saved <Mono>amanchain-vX.Y.Z-*.zip</Mono>.</li>
          <li>Upload it and extract at the project root — it replaces <Mono>src/</Mono>, <Mono>db/</Mono>, <Mono>public/</Mono> and every config file with the newest version.</li>
          <li>Run <Mono>bun install</Mono> then <Mono>bun run dev</Mono>.</li>
          <li>Verify: version shows <Mono>v{view?.version ?? 'X.Y.Z'}</Mono>, your wallet has its full balance, the Bridge shows the same real vault addresses, the DEX market is live.</li>
        </ol>
        <p className="text-[0.65rem] text-muted-foreground border-t border-border pt-2">
          Rule of thumb: <b className="text-foreground">snapshot → commit → announce</b>. A release is not done until the snapshot exists. The full procedure ships inside every archive as <Mono>RESTORE.md</Mono>.
        </p>
      </div>
    </div>
  );
}

// ---------- Governance parameters (admin_param txs) ----------
function GovernancePanel({ net, auth, password }: { net: Net; auth: AdminVerifyT; password: string }) {
  const { session } = useSession();
  const [fee, setFee] = useState(String(auth.params.tokenCreateFee));
  const [burn, setBurn] = useState(String(auth.params.feeBurnRate * 100));
  const [busy, setBusy] = useState<string | null>(null);

  const send = async (param: string, value: number | boolean, tag: string) => {
    if (!session) return;
    setBusy(tag);
    try {
      await post(`/api/tx?${netQ(net)}`, {
        walletId: session.walletId, password, type: 'admin_param', param, value,
      });
      toast.success('Governance transaction submitted — effective once mined');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Governance failed');
    } finally {
      setBusy(null);
    }
  };

  const faucetSwitch = async (v: boolean) => {
    if (net !== 'testnet') { toast.error('The faucet exists only on Testnet'); return; }
    await send('faucetOn', v, 'faucet');
  };

  return (
    <div className="glass p-4 space-y-4">
      <div className="flex items-center gap-2 text-sm font-bold"><SlidersHorizontal className="size-4 text-primary" /> Protocol parameters</div>
      <p className="text-[0.68rem] text-muted-foreground">
        Parameters take effect at the next mined block and are enforced by consensus validation — not by configuration files.
      </p>
      <div className="space-y-1">
        <span className="text-xs text-muted-foreground">Token deployment fee (AMAN) — current {auth.params.tokenCreateFee}</span>
        <div className="flex gap-2">
          <input value={fee} onChange={e => setFee(e.target.value.replace(/[^0-9.]/g, ''))} inputMode="decimal" dir="ltr"
            className="flex-1 rounded-lg bg-secondary ring-1 ring-border px-3 py-2 text-sm" />
          <button disabled={busy === 'fee'} onClick={() => send('tokenCreateFee', Number(fee), 'fee')}
            className="rounded-lg bg-primary text-primary-foreground font-bold px-4 text-xs disabled:opacity-40">Submit</button>
        </div>
      </div>
      <div className="space-y-1">
        <span className="text-xs text-muted-foreground">Fee burn rate (%) — current {(auth.params.feeBurnRate * 100).toFixed(0)}%</span>
        <div className="flex gap-2">
          <input value={burn} onChange={e => setBurn(e.target.value.replace(/[^0-9.]/g, ''))} inputMode="decimal" dir="ltr"
            className="flex-1 rounded-lg bg-secondary ring-1 ring-border px-3 py-2 text-sm" />
          <button disabled={busy === 'burn'} onClick={() => send('feeBurnRate', Number(burn) / 100, 'burn')}
            className="rounded-lg bg-primary text-primary-foreground font-bold px-4 text-xs disabled:opacity-40">Submit</button>
        </div>
      </div>
      <div className="flex items-center justify-between rounded-lg bg-muted/40 p-3">
        <div>
          <div className="text-xs font-bold">Testnet faucet</div>
          <Mono>{auth.params.faucetOn ? 'Enabled' : 'Disabled'}</Mono>
        </div>
        <div className="flex gap-2">
          <button disabled={busy === 'faucet' || net !== 'testnet'} onClick={() => faucetSwitch(true)}
            className="rounded-lg ring-1 ring-border px-3 py-1.5 text-xs font-bold disabled:opacity-40">On</button>
          <button disabled={busy === 'faucet' || net !== 'testnet'} onClick={() => faucetSwitch(false)}
            className="rounded-lg ring-1 ring-border px-3 py-1.5 text-xs font-bold disabled:opacity-40">Off</button>
        </div>
      </div>
      <p className="text-[0.65rem] text-muted-foreground border-t border-border pt-3">
        Signed automatically with your founder session — the transaction must still be mined into a block.
      </p>
    </div>
  );
}

// ---------- Token oversight (admin_freeze + admin_hide txs) ----------
function FreezePanel({ net, auth, password }: { net: Net; auth: AdminVerifyT; password: string }) {
  const { session } = useSession();
  const { data, refresh } = usePoll<{ tokens: TokenT[]; stats?: { hidden: number; frozen: number } }>(
    async () => post(`/api/admin/tokens?${netQ(net)}`, { net, walletId: session?.walletId, password }),
    5000, [net, session?.walletId]);
  const [busy, setBusy] = useState<string | null>(null);

  const act = async (type: 'admin_freeze' | 'admin_hide', symbol: string, flag: boolean, verb: string) => {
    if (!session) return;
    setBusy(symbol + type + String(flag));
    try {
      await post(`/api/tx?${netQ(net)}`, {
        walletId: session.walletId, password, type, symbol,
        ...(type === 'admin_freeze' ? { freeze: flag } : { hide: flag }),
      });
      toast.success(`${verb} of ${symbol} submitted — effective once mined`);
      setTimeout(refresh, 1500);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Governance failed');
    } finally {
      setBusy(null);
    }
  };

  const tokens = data?.tokens ?? [];

  return (
    <div className="glass p-4 space-y-3">
      <div className="flex items-center gap-2 text-sm font-bold"><Snowflake className="size-4 text-sky-400" /> Token oversight</div>
      <p className="text-[0.65rem] text-muted-foreground">
        <b>Freeze</b> halts every operation of a token in consensus. <b>Hide</b> removes it from every public
        surface — token lists, the DEX, wallets and the explorer — while the ledger stays intact and reversible.
      </p>
      <div className="space-y-2 max-h-80 overflow-y-auto">
        {tokens.length === 0 && <div className="text-xs text-muted-foreground">No tokens deployed on this network.</div>}
        {tokens.map(t => (
          <div key={t.symbol} className="flex items-center justify-between gap-2 rounded-lg bg-muted/40 px-3 py-2">
            <div className="min-w-0">
              <div className="text-xs font-bold flex items-center gap-1.5">
                {t.symbol}
                {t.frozen && <span className="text-rose-400">· FROZEN</span>}
                {t.hidden && <span className="text-amber-400">· HIDDEN</span>}
              </div>
              <Mono className="truncate text-[0.62rem]">{t.name} · supply {fmtAmt(t.totalSupply)}</Mono>
            </div>
            <div className="flex gap-1.5 shrink-0">
              <button
                onClick={() => act('admin_hide', t.symbol, !t.hidden, t.hidden ? 'Show' : 'Hide')}
                disabled={busy !== null}
                className={`rounded-lg px-2.5 py-1.5 text-[0.66rem] font-bold ring-1 transition disabled:opacity-40 ${t.hidden ? 'ring-emerald-500/40 text-emerald-400' : 'ring-amber-500/40 text-amber-400'}`}>
                {busy === t.symbol + 'admin_hide' + String(!t.hidden) ? <Loader2 className="size-3.5 animate-spin" /> : t.hidden ? 'Show' : 'Hide'}
              </button>
              <button
                onClick={() => act('admin_freeze', t.symbol, !t.frozen, t.frozen ? 'Unfreeze' : 'Freeze')}
                disabled={busy !== null}
                className={`rounded-lg px-2.5 py-1.5 text-[0.66rem] font-bold ring-1 transition disabled:opacity-40 ${t.frozen ? 'ring-emerald-500/40 text-emerald-400' : 'ring-rose-500/40 text-rose-400'}`}>
                {busy === t.symbol + 'admin_freeze' + String(!t.frozen) ? <Loader2 className="size-3.5 animate-spin" /> : t.frozen ? 'Unfreeze' : 'Freeze'}
              </button>
            </div>
          </div>
        ))}
      </div>
      <p className="text-[0.65rem] text-muted-foreground border-t border-border pt-3">
        {(data?.stats?.hidden ?? 0) > 0 && <span className="text-amber-400">{data?.stats?.hidden} hidden · </span>}
        Freezing and hiding are enforced by consensus transactions signed with your founder key — nothing is editable by hand.
      </p>
    </div>
  );
}

// ---------- API keys ----------
function KeysPanel({ net, auth, password }: { net: Net; auth: AdminVerifyT; password: string }) {
  const { session } = useSession();
  const [keys, setKeys] = useState<ApiKeyT[]>([]);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [role, setRole] = useState<'public' | 'admin'>('public');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const call = async (action: string, extra?: Record<string, unknown>) => {
    if (!session) return;
    setBusy(true);
    try {
      const r = await post<{ keys?: ApiKeyT[]; key?: string; warning?: string }>('/api/admin/keys', {
        net, action, walletId: session.walletId, password, ...extra,
      });
      if (r.keys) setKeys(r.keys);
      if (r.key) {
        setNewKey(r.key);
        toast.success('API key created — copy it now, it is shown once');
      } else {
        toast.success(`Key ${action === 'revoke' ? 'revoked' : action + 'ed'}`);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Key operation failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="glass p-4 space-y-4">
      <div className="flex items-center gap-2 text-sm font-bold"><KeyRound className="size-4 text-amber-400" /> Public API keys</div>
      <p className="text-[0.68rem] text-muted-foreground">
        Public keys identify external integrations against <Mono>/api/v1</Mono>. Admin keys can also be required for
        write endpoints if you enable that at the gateway. Keys are stored as SHA3 hashes — the plaintext appears once.
      </p>

      <div className="grid md:grid-cols-4 gap-3">
        <label className="space-y-1 md:col-span-2">
          <span className="text-xs text-muted-foreground">Key name</span>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="exchange-integration"
            className="w-full rounded-lg bg-secondary ring-1 ring-border px-3 py-2 text-sm" />
        </label>
        <label className="space-y-1">
          <span className="text-xs text-muted-foreground">Role</span>
          <select value={role} onChange={e => setRole(e.target.value as 'public' | 'admin')}
            className="w-full rounded-lg bg-secondary ring-1 ring-border px-3 py-2 text-sm">
            <option value="public">public</option>
            <option value="admin">admin</option>
          </select>
        </label>
      </div>
      <div className="flex gap-2">
        <button disabled={busy} onClick={() => call('create', { name, role })}
          className="rounded-xl bg-primary text-primary-foreground font-black px-5 py-2.5 text-sm disabled:opacity-40">Create key</button>
        <button disabled={busy} onClick={() => call('list')}
          className="rounded-xl ring-1 ring-border font-bold px-5 py-2.5 text-sm disabled:opacity-40">List keys</button>
      </div>

      {newKey && (
        <div className="rounded-xl bg-amber-500/5 ring-1 ring-amber-500/30 p-3 space-y-1.5">
          <div className="text-xs font-bold text-amber-400">New key — shown only once</div>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-[0.72rem] break-all font-mono" dir="ltr">{newKey}</code>
            <button onClick={async () => {
              try { await navigator.clipboard.writeText(newKey); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* ignore */ }
            }} className="rounded-lg ring-1 ring-border p-2 hover:ring-primary/40" aria-label="Copy key">
              {copied ? <Check className="size-4 text-emerald-400" /> : <Copy className="size-4" />}
            </button>
          </div>
        </div>
      )}

      {keys.length > 0 && (
        <div className="space-y-2">
          {keys.map(k => (
            <div key={k.id} className="flex items-center justify-between rounded-lg bg-muted/40 px-3 py-2 text-xs">
              <div className="min-w-0">
                <div className="font-bold">{k.name} <span className={`ml-1 rounded-full px-1.5 py-0.5 text-[0.6rem] ${k.role === 'admin' ? 'bg-rose-500/10 text-rose-400' : 'bg-sky-500/10 text-sky-400'}`}>{k.role}</span></div>
                <Mono>{k.prefix}… · created {timeAgo(k.createdAt)} · {k.lastUsedAt ? `used ${timeAgo(k.lastUsedAt)}` : 'never used'}{k.revoked ? ' · REVOKED' : ''}</Mono>
              </div>
              {!k.revoked && (
                <button onClick={() => call('revoke', { id: k.id })} disabled={busy}
                  className="rounded-lg px-3 py-1.5 text-[0.68rem] font-bold ring-1 ring-rose-500/40 text-rose-400 disabled:opacity-40">
                  Revoke
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Public API docs ----------
const ENDPOINTS: Array<{ m: string; p: string; d: string }> = [
  { m: 'GET', p: '/api/v1/health', d: 'Node liveness, version and network registry' },
  { m: 'GET', p: '/api/v1/chain', d: 'Height, difficulty, supply, burns, market, governance params' },
  { m: 'GET', p: '/api/v1/blocks?offset=0&limit=20', d: 'Blocks, newest first (paginated)' },
  { m: 'GET', p: '/api/v1/blocks/{height|hash}', d: 'Single block with full transaction summaries' },
  { m: 'GET', p: '/api/v1/txs?address=AM…', d: 'Transactions (mined + pending), filterable by address' },
  { m: 'GET', p: '/api/v1/txs/{id}', d: 'Full transaction incl. complete quantum-shield hex' },
  { m: 'GET', p: '/api/v1/addresses/{address}', d: 'Balance, staking, token assets, LP positions, history' },
  { m: 'GET', p: '/api/v1/tokens', d: 'All AMT-20 tokens with supply and holders' },
  { m: 'GET', p: '/api/v1/tokens/{symbol}', d: 'Token detail: top holders and transfers' },
  { m: 'GET', p: '/api/v1/pools', d: 'AMM pools: reserves, price, TVL, fees, burns' },
  { m: 'GET', p: '/api/v1/price', d: 'Canonical AMAN price from the deepest pool' },
  { m: 'POST', p: '/api/v1/tx', d: 'Broadcast a fully signed transaction (external wallets sign locally)' },
];

function ApiDocs() {
  const [copied, setCopied] = useState(false);
  const curl = `curl -s "$BASE_URL/api/v1/price" | jq
curl -s "$BASE_URL/api/v1/chain?net=mainnet" -H "Authorization: Bearer am_public_…" | jq`;
  return (
    <div className="glass p-4 space-y-3">
      <div className="flex items-center gap-2 text-sm font-bold"><Terminal className="size-4 text-cyan-400" /> Public API — open the network to the world</div>
      <p className="text-[0.68rem] text-muted-foreground">
        Every endpoint below is public, rate-limited (120 req/min per IP, 30/min for broadcasts) and CORS-open so
        exchanges, explorers and wallet apps can integrate directly. Network selection via <Mono>?net=</Mono> or the
        <Mono> X-Aman-Net</Mono> header.
      </p>
      <div className="rounded-xl ring-1 ring-border overflow-hidden">
        <table className="w-full text-xs">
          <tbody>
            {ENDPOINTS.map(e => (
              <tr key={e.p} className="border-b border-border/60 last:border-0">
                <td className={`px-3 py-2 font-black w-14`} style={{ color: e.m === 'POST' ? '#fbbf24' : '#34d399' }}>{e.m}</td>
                <td className="px-3 py-2 font-mono" dir="ltr">{e.p}</td>
                <td className="px-3 py-2 text-muted-foreground hidden md:table-cell">{e.d}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="rounded-xl bg-zinc-950 ring-1 ring-border p-3 relative">
        <div className="absolute top-2 right-2"><CopyBtn text={curl} /></div>
        <pre className="text-[0.7rem] text-emerald-300/90 overflow-x-auto" dir="ltr">{curl}</pre>
      </div>
      <p className="text-[0.65rem] text-muted-foreground">
        External signers: build the canonical message <Mono>AMAN-v2|chainId|type|from|to|amount|fee|nonce|timestamp|data|tier</Mono>,
        sign it with Ed25519 <b>and</b> ML-DSA-87, then POST the full transaction object to <Mono>/api/v1/tx</Mono>.
        The node validates both signatures against consensus and broadcasts into the mempool.
      </p>
    </div>
  );
}

// ============================================================
// BridgePanel — cross-chain control (v2.7.4)
// Per-network watcher health, REAL vault balances, custody float
// funding, record actions (refund stuck releases, attach BTC txids).
// ============================================================
interface BridgeHealth { net: string; online: boolean; latencyMs: number | null; tip: string | null; error: string | null; }
interface BridgeNetView {
  net: string; name: string; nativeSymbol: string; vaultAddress: string; explorerAddr: string;
  health: BridgeHealth; balances: { asset: string; amount: number }[] | null; balanceError: string | null;
}
interface BridgeRecT {
  id: string; net: string; dir: 'in' | 'out'; asset: string; networkName: string;
  amount: number; credit: number; amanAddr: string; status: string; note: string | null;
  externalTxid: string | null; explorerTx: string | null; attempts: number; updatedAt: number;
}
interface BridgeAdminView {
  admin: boolean; feeBps: number; networks: BridgeNetView[];
  custody: Record<string, { address: string; aman: number; assets: Record<string, number> }>;
  records: BridgeRecT[];
}

function BridgePanel({ net, auth, walletId, password }: { net: Net; auth: AdminVerifyT; walletId: string; password: string }) {
  const [view, setView] = useState<BridgeAdminView | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [btcTxids, setBtcTxids] = useState<Record<string, string>>({});
  const [fund, setFund] = useState<Record<string, string>>({});
  const [err, setErr] = useState<string | null>(null);

  const load = async () => {
    try {
      const r = await post<BridgeAdminView>('/api/bridge', { net, action: 'admin_overview', walletId: walletId, password });
      setView(r); setErr(null);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Bridge overview failed'); }
  };
  useEffect(() => { void load(); const t = setInterval(() => { void load(); }, 8000); return () => clearInterval(t); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [net]);

  const refund = async (recordId: string) => {
    setBusy(recordId);
    try {
      await post('/api/bridge', { net, action: 'admin_refund', walletId: walletId, password, recordId });
      toast.success('Refunded — wrapped tokens returned to the user');
      await load();
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Refund failed'); }
    finally { setBusy(null); }
  };
  const attachBtc = async (recordId: string) => {
    const txid = (btcTxids[recordId] ?? '').trim();
    if (!txid) { toast.error('Paste the real 64-hex Bitcoin txid after broadcasting from the vault'); return; }
    setBusy(recordId);
    try {
      await post('/api/bridge', { net, action: 'admin_btc_release', walletId: walletId, password, recordId, txid });
      toast.success('BTC release marked with the real txid');
      await load();
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Attach failed'); }
    finally { setBusy(null); }
  };
  const fundCustody = async (symbol: string, amount: string) => {
    const custody = view?.custody?.[net];
    if (!custody || !(Number(amount) > 0)) { toast.error('Enter an amount'); return; }
    setBusy(`fund-${symbol}`);
    try {
      if (symbol === 'AMAN') {
        await post(`/api/tx?net=${net}`, { walletId: walletId, password, type: 'transfer', to: custody.address, amount: Number(amount) });
      } else {
        await post(`/api/tx?net=${net}`, { walletId: walletId, password, type: 'token_transfer', symbol, to: custody.address, amount: Number(amount) });
      }
      toast.success(`Custody funded with ${amount} ${symbol} — mined on-chain`);
      setFund(f => ({ ...f, [symbol]: '' }));
      await load();
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Funding failed'); }
    finally { setBusy(null); }
  };

  const floatSymbols = ['AMAN', 'SOL', 'ETH', 'BNB', 'BTC', 'USDC', 'USDT'];

  // Vault recovery kit — the founder downloads the REAL vault private keys
  // (wallet-importable formats + master seed). Even if the platform is fully
  // reset, these keys control the deposits: import → extract funds directly.
  const [kit, setKit] = useState<{
    masterSeedHex: string; masterNote: string; instructions: string[];
    vaults: Array<{ net: string; name: string; address: string; importFormats: Array<{ wallet: string; format: string; value: string }> }>;
  } | null>(null);
  const [kitBusy, setKitBusy] = useState(false);
  const downloadVaultKit = async () => {
    setKitBusy(true);
    try {
      const r = await post<NonNullable<typeof kit>>(`/api/bridge?${netQ(net)}`, { net, action: 'admin_vault_kit', walletId, password });
      setKit(r);
      const blob = new Blob([JSON.stringify(r, null, 2)], { type: 'application/json' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `amanchain-vault-recovery-kit-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 4000);
      toast.success('Vault recovery kit downloaded — store it offline, it controls the bridge deposits');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Vault kit failed');
    } finally { setKitBusy(false); }
  };

  return (
    <div className="space-y-4">
      {/* Vault recovery kit — funds-survival guarantee */}
      <div className="glass p-5 space-y-3 ring-1 ring-amber-500/30">
        <div className="font-black flex items-center gap-2"><Vault className="size-4 text-amber-400" /> Bridge vault recovery keys — ضمان استرجاع الأموال</div>
        <p className="text-[0.7rem] text-muted-foreground leading-relaxed">
          يصدّر هذا الزر المفاتيح الخاصة الحقيقية لخزائن الجسر العشر (بصيغ قابلة للاستيراد مباشرة: Phantom لسولانا، MetaMask لسلاسل EVM، WIF لبيتكوين/لايتكوين) + البذرة الرئيسية التي تُعيد توليد كل الخزائن.
          حتى لو أُعيد ضبط المنصة بالكامل — استورد المفتاح في المحفظة المناسبة واسحب كل العملات المودعة في الخزنة مباشرة على الشبكة الخارجية. الملف نفسه محفوظ تلقائياً في <Mono>db/vault-recovery.json</Mono> ويدخل في كل نسخة snapshot.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => void downloadVaultKit()} disabled={kitBusy}
            className="inline-flex items-center gap-2 rounded-xl bg-amber-500 text-zinc-950 font-black px-4 py-2.5 text-xs hover:bg-amber-400 transition disabled:opacity-40">
            {kitBusy ? <Loader2 className="size-4 animate-spin" /> : <HardDriveDownload className="size-4" />}
            تحميل مفاتيح خزائن الجسر (Vault Kit)
          </button>
          <span className="text-[0.62rem] text-muted-foreground">Founder-only · keep it OFFLINE — whoever holds these keys controls the deposits</span>
        </div>
        {kit && (
          <div className="space-y-1.5 pt-1">
            {kit.vaults.map(v => (
              <div key={v.net} className="flex flex-wrap items-center gap-2 rounded-lg bg-muted/40 px-3 py-1.5 text-[0.64rem]">
                <b className="w-24">{v.name}</b>
                <Mono className="text-muted-foreground">{v.address.slice(0, 22)}…</Mono>
                <span className="text-emerald-400 font-bold">{v.importFormats[0]?.wallet}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="glass p-5 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="font-black flex items-center gap-2">Bridge — cross-chain control</div>
          <p className="text-[0.68rem] text-muted-foreground">Real external networks: Solana · Ethereum · BNB · Polygon · Arbitrum · Base · Avalanche · Optimism · Bitcoin · Litecoin. Watcher health, real vault balances and custody float.</p>
        </div>
        <button onClick={() => void load()} className="rounded-lg ring-1 ring-border px-3 py-1.5 text-xs font-bold hover:ring-primary/40 transition">Refresh</button>
      </div>
      {err && <div className="rounded-xl bg-rose-500/10 text-rose-400 ring-1 ring-rose-500/30 p-2.5 text-xs font-bold">{err}</div>}

      {/* Watcher health per network */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {(view?.networks ?? []).map(n => (
          <div key={n.net} className="rounded-xl ring-1 ring-border p-3 space-y-1.5">
            <div className="flex items-center justify-between gap-2">
              <span className="font-black text-xs">{n.name}</span>
              <span className={`text-[0.62rem] font-black inline-flex items-center gap-1 ${n.health.online ? 'text-emerald-400' : 'text-rose-400'}`}>
                <span className={`size-1.5 rounded-full ${n.health.online ? 'bg-emerald-400 pulse-dot' : 'bg-rose-400'}`} />
                {n.health.online ? 'ONLINE' : 'OFFLINE'}
              </span>
            </div>
            <div className="text-[0.65rem] text-muted-foreground" dir="ltr">{n.health.online ? `${n.health.latencyMs}ms · ${n.health.tip ?? ''}` : (n.health.error ?? 'unreachable').slice(0, 70)}</div>
            <div className="text-[0.68rem]">
              {(n.balances ?? []).map(b => (
                <div key={b.asset} className="flex justify-between gap-2"><span className="text-muted-foreground">{b.asset}</span><b>{typeof b.amount === 'number' && Number.isFinite(b.amount) ? (b.amount >= 1 ? b.amount.toFixed(4) : b.amount.toFixed(6)) : 'n/a'}</b></div>
              ))}
              {!n.balances && <span className="text-amber-400">{n.balanceError ? 'balance n/a' : 'loading…'}</span>}
            </div>
          </div>
        ))}
      </div>

      {/* Custody float funding */}
      <div className="rounded-xl ring-1 ring-border p-3.5 space-y-2">
        <div className="text-xs font-black">Custody float — {net}</div>
        <div className="flex flex-wrap gap-2 text-[0.68rem]">
          {Object.entries(view?.custody?.[net]?.assets ?? {}).filter(([, v]) => v > 0).map(([k, v]) => (
            <span key={k} className="rounded-full ring-1 ring-emerald-500/30 bg-emerald-500/5 text-emerald-400 px-2.5 py-1 font-bold">{k} {fmtAmt(v)}</span>
          ))}
          <span className="rounded-full ring-1 ring-border px-2.5 py-1 font-bold">AMAN {fmtAmt(view?.custody?.[net]?.aman ?? 0)}</span>
          <Mono className="text-[0.62rem] text-muted-foreground self-center">{view?.custody?.[net]?.address ?? ''}</Mono>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 pt-1">
          {floatSymbols.map(sym => (
            <div key={sym} className="flex items-center gap-1">
              <input value={fund[sym] ?? ''} onChange={e => setFund(f => ({ ...f, [sym]: e.target.value }))} placeholder={sym} dir="ltr" inputMode="decimal"
                className="w-full rounded-lg bg-background ring-1 ring-input px-2 py-1.5 text-[0.68rem] outline-none focus:ring-emerald-500/50" />
              <button onClick={() => void fundCustody(sym, fund[sym] ?? '')} disabled={busy === `fund-${sym}`}
                className="rounded-lg bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/40 px-2 py-1.5 text-[0.62rem] font-black disabled:opacity-50">{busy === `fund-${sym}` ? '…' : 'Fund'}</button>
            </div>
          ))}
        </div>
        <p className="text-[0.62rem] text-muted-foreground">Funding transfers real AMT-20 wrapped assets from your founder wallet into the bridge custody wallet — deposits credit from this float; withdrawals return burned tokens to it.</p>
      </div>

      {/* Records */}
      <div className="rounded-xl ring-1 ring-border overflow-hidden">
        <table className="w-full text-xs">
          <thead><tr className="border-b border-border/60 text-muted-foreground text-left">
            <th className="px-3 py-2">Record</th><th className="px-3 py-2">Amount</th><th className="px-3 py-2">Status</th><th className="px-3 py-2 hidden md:table-cell">Note</th><th className="px-3 py-2">Action</th>
          </tr></thead>
          <tbody>
            {(view?.records ?? []).slice(0, 20).map(r => (
              <tr key={r.id} className="border-b border-border/60 last:border-0 align-top">
                <td className="px-3 py-2">
                  <div className="font-black">{r.dir === 'in' ? '↓ in' : '↑ out'} {r.networkName}</div>
                  <Mono className="text-[0.6rem] text-muted-foreground">{r.id}</Mono>
                </td>
                <td className="px-3 py-2 font-bold" dir="ltr">{r.credit} {r.asset}</td>
                <td className="px-3 py-2"><span className={`font-black ${r.status === 'released' || r.status === 'settled' || r.status === 'refunded' ? 'text-emerald-400' : r.status === 'rejected' ? 'text-rose-400' : r.status === 'queued' ? 'text-violet-400' : 'text-amber-400'}`}>{r.status}</span></td>
                <td className="px-3 py-2 text-muted-foreground hidden md:table-cell max-w-[240px]">{r.note}</td>
                <td className="px-3 py-2 space-y-1.5">
                  {r.dir === 'out' && r.status !== 'released' && r.status !== 'refunded' && (
                    <button onClick={() => void refund(r.id)} disabled={busy === r.id}
                      className="rounded-lg bg-rose-500/10 text-rose-400 ring-1 ring-rose-500/30 px-2 py-1 text-[0.62rem] font-black disabled:opacity-50">Refund</button>
                  )}
                  {r.status === 'queued' && (
                    <div className="flex items-center gap-1">
                      <input value={btcTxids[r.id] ?? ''} onChange={e => setBtcTxids(m => ({ ...m, [r.id]: e.target.value }))} placeholder="real BTC txid" dir="ltr"
                        className="w-32 rounded-lg bg-background ring-1 ring-input px-2 py-1 text-[0.62rem] outline-none" />
                      <button onClick={() => void attachBtc(r.id)} disabled={busy === r.id}
                        className="rounded-lg bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/40 px-2 py-1 text-[0.62rem] font-black disabled:opacity-50">Attach</button>
                    </div>
                  )}
                  {r.explorerTx && <a href={r.explorerTx} target="_blank" rel="noreferrer" className="text-[0.62rem] text-sky-400 hover:underline">external tx ↗</a>}
                </td>
              </tr>
            ))}
            {!view?.records.length && <tr><td colSpan={5} className="px-3 py-4 text-center text-muted-foreground">No bridge records yet</td></tr>}
          </tbody>
        </table>
      </div>
      </div>
    </div>
  );
}
